import { api } from '@/lib/axios';

export interface Identification {
  closed_time: string | null;
  ticket_name: string | null;
  guests_count: number | null;
  card_last_4: string | null;
  card_brand: string | null;
  payment_type: string | null;
}

export interface LineItem {
  remote_item_id: string | null;
  name: string;
  quantity: number;
  total_money: number;
  /** True when the dish exists in Kombu and has at least one photo */
  onboarded: boolean;
  /** Kombu item id when the dish exists (it may still have no photo) */
  item_id?: string | null;
}

export interface MemoryInfo {
  id: string;
  slug: string;
  status: 'draft' | 'queued' | 'rendering' | 'done' | 'failed';
  video_url: string | null;
  video_generated_at: string | null;
  video_views_count: number;
  progress: number | null;
  error_message: string | null;
  print_count: number;
}

export interface DeskOrder {
  id: string;
  provider_order_id: string;
  identification: Identification;
  line_items: LineItem[];
  total_money: {
    amount: number;
    currency: string;
  } | null;
  state: string;
  created_at: string;
  memory: MemoryInfo | null;
}

export type ServiceShift = 'lunch' | 'dinner' | 'unknown';

export type ShiftFilter = 'current' | 'all';

/** Logo para el ticket, legible también por el personal (spec 012) */
export interface DeskRestaurant {
  logo_url: string | null;
}

export interface OrdersListResponse {
  orders: DeskOrder[];
  current_shift: ServiceShift;
  restaurant?: DeskRestaurant;
}

export interface SyncResponse {
  message: string;
  status: string;
}

export interface SyncStatus {
  status: 'idle' | 'running' | 'completed' | 'failed' | 'error';
  progress?: number;
  total?: number;
  synced?: number;
  failed?: number;
  started_at?: string;
  completed_at?: string;
  failed_at?: string;
  error?: string;
}

export const deskService = {
  async getOrders(shift?: ShiftFilter): Promise<OrdersListResponse> {
    const params = new URLSearchParams();
    if (shift) params.append('shift', shift);
    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await api.get<OrdersListResponse>(`/v1/admin/orders${query}`);
    return response.data;
  },

  async syncOrders(): Promise<SyncResponse> {
    const response = await api.post<SyncResponse>('/v1/admin/orders/sync');
    return response.data;
  },

  async getSyncStatus(): Promise<SyncStatus> {
    const response = await api.get<SyncStatus>('/v1/admin/orders/sync/status');
    return response.data;
  },

  async printOrder(orderId: string, count: number = 1): Promise<{ memory: { id: string; slug: string; status: string; print_count: number }; qr_url: string }> {
    const response = await api.post(`/v1/admin/orders/${orderId}/print`, { count });
    return response.data;
  },

  async regenerateVideo(orderId: string): Promise<{ memory: { id: string; slug: string; status: string; print_count: number } }> {
    const response = await api.post(`/v1/admin/orders/${orderId}/regenerate-video`);
    return response.data;
  },
};
